import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { uploadOrderProofToTemp } from "../../api/ordersApi";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { City } from "../../types/city";
import type { Customer } from "../../types/customer";
import type { OperationsTeam } from "../../types/operationsTeam";
import {
  type OrderFormValues,
  type OrderProofAttachment,
  combineDepartureTimestamp,
  emptyOrderFormValues,
  validateOrderForm,
} from "../../types/order";
import type { Truck } from "../../types/truck";
import { type User, UserRole } from "../../types/user";
import {
  findEntityById,
  getEntityOptionKey,
  isEntityOptionEqual,
} from "../../utils/entityAutocomplete";
import {
  buildRevisionExpenseKeySet,
  expenseRevisionFieldSx,
  isAdditionalExpenseRevisionKey,
  isSingletonExpenseRevisionKey,
} from "../../utils/expenseRevisionFieldStyle";
import { formatLicensePlateInput } from "../../utils/formatLicensePlate";
import {
  formatDepartureDateString,
  formatDepartureTimeString,
  isCompleteDepartureDate,
  parseDepartureDateString,
  parseDepartureTimeString,
} from "../../utils/orderDepartureDateTime";
import DetailSectionCard from "../common/DetailSectionCard";
import RupiahAmountField from "../common/RupiahAmountField";

type TranslationKeys = keyof typeof translations.en;

export const ORDER_FORM_ID = "order-form";

export interface OrderFormMeta {
  createdAtLabel?: string;
  createdByLabel?: string;
  reportReadableId?: string;
  reportUrl?: string;
}

interface OrderFormProps {
  initialValues?: OrderFormValues;
  teams: OperationsTeam[];
  cities: City[];
  trucks: Truck[];
  users: User[];
  customers: Customer[];
  isEdit?: boolean;
  canSelectTeam?: boolean;
  loading?: boolean;
  error?: string | null;
  meta?: OrderFormMeta;
  /** Expense review keys flagged for revision (shown with amber highlight). */
  revisionExpenseKeys?: string[];
  /** Persisted additional expense ids aligned with form rows for revision highlighting. */
  additionalExpenseIds?: string[];
  /** When true, Cancel/Save are rendered by the page header via form= attribute. */
  hideActions?: boolean;
  existingProofAttachments?: OrderProofAttachment[];
  onSubmit: (
    values: OrderFormValues,
    extras?: { pendingProofKeys: string[]; keptProofAttachmentIds: string[] },
  ) => Promise<void>;
  onCancel: () => void;
}

const ReadOnlyField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }} component="div">
      {value}
    </Typography>
  </Box>
);

const OrderForm: React.FC<OrderFormProps> = ({
  initialValues,
  teams,
  cities,
  trucks,
  users,
  customers,
  isEdit = false,
  canSelectTeam = false,
  loading = false,
  error = null,
  meta,
  revisionExpenseKeys,
  additionalExpenseIds = [],
  hideActions = false,
  existingProofAttachments = [],
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const revisionKeys = useMemo(
    () => buildRevisionExpenseKeySet(revisionExpenseKeys),
    [revisionExpenseKeys],
  );
  const [values, setValues] = useState<OrderFormValues>(initialValues || emptyOrderFormValues());
  const [keptExistingProofs, setKeptExistingProofs] =
    useState<OrderProofAttachment[]>(existingProofAttachments);
  const [pendingProofs, setPendingProofs] = useState<
    Array<{ localId: string; filename: string; key?: string }>
  >([]);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [proofError, setProofError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // Local picker values so mid-typing (e.g. year "20" of "2026") does not reset day/month.
  const [departureDatePickerValue, setDepartureDatePickerValue] = useState<Date | null>(() =>
    parseDepartureDateString(initialValues?.departureDate || ""),
  );
  const [departureTimePickerValue, setDepartureTimePickerValue] = useState<Date | null>(() =>
    parseDepartureTimeString(initialValues?.departureTime || ""),
  );

  useEffect(() => {
    if (!initialValues) {
      return;
    }
    setValues(initialValues);
    setDepartureDatePickerValue(parseDepartureDateString(initialValues.departureDate));
    setDepartureTimePickerValue(parseDepartureTimeString(initialValues.departureTime));
  }, [initialValues]);

  const existingProofAttachmentIds = existingProofAttachments
    .map((attachment) => attachment.id)
    .join(",");

  // biome-ignore lint/correctness/useExhaustiveDependencies: only resync when attachment ids change so local removals are kept
  useEffect(() => {
    setKeptExistingProofs(existingProofAttachments);
  }, [existingProofAttachmentIds]);

  const drivers = useMemo(
    () => users.filter((user) => user.role === UserRole.DRIVER && !user.deactivated),
    [users],
  );

  const selectedLoadCities = useMemo(
    () =>
      values.loadCityIds
        .map((cityId) => findEntityById(cities, cityId))
        .filter((city): city is City => !!city),
    [values.loadCityIds, cities],
  );

  const selectedUnloadCities = useMemo(
    () =>
      values.unloadCityIds
        .map((cityId) => findEntityById(cities, cityId))
        .filter((city): city is City => !!city),
    [values.unloadCityIds, cities],
  );

  const assignedTeam = useMemo(
    () => teams.find((team) => team._id === values.teamOperationsId) || null,
    [teams, values.teamOperationsId],
  );

  const expenseMobValue = Number(values.expenseMob) || 0;
  const showMobRoute = expenseMobValue > 0;

  const updateValues = (patch: Partial<OrderFormValues>) => {
    setValues((prev) => ({ ...prev, ...patch }));
  };

  const handleAdditionalChange = (index: number, field: "amount" | "notes", value: string) => {
    setValues((prev) => ({
      ...prev,
      expenseAdditional: prev.expenseAdditional.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const handleAddAdditionalRow = () => {
    setValues((prev) => ({
      ...prev,
      expenseAdditional: [...prev.expenseAdditional, { amount: "", notes: "" }],
    }));
  };

  const handleRemoveAdditionalRow = (index: number) => {
    setValues((prev) => ({
      ...prev,
      expenseAdditional: prev.expenseAdditional.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (departureDatePickerValue && !isCompleteDepartureDate(departureDatePickerValue)) {
      setValidationErrors(["orders.validation.departureInvalid"]);
      return;
    }
    const errors = validateOrderForm(values, {
      requireTeam: canSelectTeam && !isEdit,
      isEdit,
    });
    setValidationErrors(errors);
    if (errors.length > 0) {
      return;
    }
    if (uploadingProof || pendingProofs.some((item) => !item.key)) {
      setProofError(t("orders.proof.uploadInProgress" as TranslationKeys));
      return;
    }
    await onSubmit(values, {
      pendingProofKeys: pendingProofs
        .map((item) => item.key)
        .filter((key): key is string => Boolean(key)),
      keptProofAttachmentIds: keptExistingProofs.map((attachment) => attachment.id),
    });
  };

  const teamField = (
    <Box data-testid={TestId.ORDER.TEAM}>
      {canSelectTeam ? (
        <FormControl fullWidth>
          <Autocomplete
            disablePortal
            options={teams}
            value={assignedTeam}
            onChange={(_, team) => updateValues({ teamOperationsId: team?._id || "" })}
            getOptionLabel={(team) => team.name}
            disabled={isEdit}
            renderInput={(params) => <TextField {...params} label={t("Team" as TranslationKeys)} />}
          />
        </FormControl>
      ) : (
        <TextField
          label={t("Team" as TranslationKeys)}
          value={assignedTeam?.name || ""}
          disabled
          fullWidth
        />
      )}
    </Box>
  );

  const actionButtons = (
    <>
      <Button
        variant="outlined"
        onClick={onCancel}
        disabled={loading}
        data-testid={TestId.ORDER.CANCEL}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        {t("Cancel" as TranslationKeys)}
      </Button>
      <Button
        type="submit"
        form={ORDER_FORM_ID}
        variant="contained"
        disabled={loading}
        data-testid={TestId.ORDER.SAVE}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        {t("Save" as TranslationKeys)}
      </Button>
    </>
  );

  return (
    <Box
      component="form"
      id={ORDER_FORM_ID}
      onSubmit={handleSubmit}
      data-testid={TestId.ORDER.FORM}
      sx={{ pb: 4 }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((validationError) => {
            const [key, index] = validationError.split(":");
            const label = t(key as TranslationKeys);
            return (
              <Typography key={validationError} variant="body2">
                {index ? `${label} (${Number(index) + 1})` : label}
              </Typography>
            );
          })}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          mb: 2,
        }}
      >
        <DetailSectionCard
          title={t("orders.generalSection" as TranslationKeys)}
          icon={<AssignmentIndOutlinedIcon fontSize="small" />}
          iconColor="#7C3AED"
        >
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            {teamField}
            {isEdit && (
              <>
                <ReadOnlyField
                  label={t("orders.createdAt" as TranslationKeys)}
                  value={meta?.createdAtLabel || "—"}
                />
                <ReadOnlyField
                  label={t("orders.createdBy" as TranslationKeys)}
                  value={meta?.createdByLabel || "—"}
                />
                <ReadOnlyField
                  label={t("orders.report" as TranslationKeys)}
                  value={
                    meta?.reportUrl && meta.reportReadableId ? (
                      <Link
                        component={RouterLink}
                        to={meta.reportUrl}
                        underline="hover"
                        data-testid={TestId.ORDER.VIEW_REPORT}
                        sx={{ fontWeight: 600 }}
                      >
                        {meta.reportReadableId}
                      </Link>
                    ) : (
                      "—"
                    )
                  }
                />
              </>
            )}
            <TextField
              label={t("orders.note" as TranslationKeys)}
              value={values.note}
              onChange={(event) => updateValues({ note: event.target.value })}
              multiline
              minRows={3}
              fullWidth
              sx={{ gridColumn: { sm: "1 / -1" } }}
              inputProps={{ "data-testid": TestId.ORDER.NOTE }}
            />
            <Box sx={{ gridColumn: { sm: "1 / -1" } }} data-testid={TestId.ORDER.PROOF}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {t("orders.proofImage" as TranslationKeys)}
              </Typography>
              {keptExistingProofs.map((attachment) => (
                <Box
                  key={attachment.id}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }} noWrap>
                    {attachment.filename}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={t("Delete" as TranslationKeys)}
                    data-testid={TestId.ORDER.PROOF_REMOVE(attachment.id)}
                    onClick={() =>
                      setKeptExistingProofs((current) =>
                        current.filter((item) => item.id !== attachment.id),
                      )
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {pendingProofs.map((item, index) => (
                <Box
                  key={item.localId}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }} noWrap>
                    {item.filename}
                    {!item.key ? "…" : ""}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={t("Delete" as TranslationKeys)}
                    data-testid={TestId.ORDER.PROOF_REMOVE(`pending-${index}`)}
                    onClick={() =>
                      setPendingProofs((current) =>
                        current.filter((pending) => pending.localId !== item.localId),
                      )
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                component="label"
                disabled={uploadingProof}
                sx={{ textTransform: "none" }}
              >
                {t("orders.proofChooseFile" as TranslationKeys)}
                <input
                  hidden
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp"
                  data-testid={TestId.ORDER.PROOF_INPUT}
                  onChange={async (event) => {
                    const files = Array.from(event.target.files ?? []);
                    event.target.value = "";
                    setProofError(null);
                    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
                    for (const file of files) {
                      if (!allowedTypes.includes(file.type)) {
                        setProofError(t("orders.proof.invalidType" as TranslationKeys));
                        return;
                      }
                      if (file.size > 5 * 1024 * 1024) {
                        setProofError(t("orders.proof.tooLarge" as TranslationKeys));
                        return;
                      }
                    }
                    setUploadingProof(true);
                    try {
                      for (const file of files) {
                        const localId = `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`;
                        setPendingProofs((current) => [
                          ...current,
                          { localId, filename: file.name },
                        ]);
                        try {
                          const uploaded = await uploadOrderProofToTemp(file);
                          setPendingProofs((current) =>
                            current.map((item) =>
                              item.localId === localId
                                ? { ...item, key: uploaded.key, filename: uploaded.filename }
                                : item,
                            ),
                          );
                        } catch (uploadErr) {
                          setPendingProofs((current) =>
                            current.filter((item) => item.localId !== localId),
                          );
                          const mapped =
                            uploadErr instanceof Error &&
                            uploadErr.message === "ORDER_PROOF_TOO_LARGE"
                              ? t("orders.proof.tooLarge" as TranslationKeys)
                              : uploadErr instanceof Error &&
                                  uploadErr.message === "ORDER_PROOF_INVALID_TYPE"
                                ? t("orders.proof.invalidType" as TranslationKeys)
                                : t("orders.proof.uploadFailed" as TranslationKeys);
                          setProofError(mapped);
                          return;
                        }
                      }
                    } finally {
                      setUploadingProof(false);
                    }
                  }}
                />
              </Button>
              {proofError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {proofError}
                </Alert>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {t("orders.proofHint" as TranslationKeys)}
              </Typography>
            </Box>
          </Box>
        </DetailSectionCard>

        <DetailSectionCard
          title={t("orders.vehicleAndRouteSection" as TranslationKeys)}
          icon={<DirectionsCarFilledOutlinedIcon fontSize="small" />}
          iconColor="#16A34A"
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              }}
            >
              <Box data-testid={TestId.ORDER.TRUCK}>
                <Autocomplete
                  disablePortal
                  options={trucks}
                  value={trucks.find((truck) => truck._id === values.truckId) || null}
                  onChange={(_, truck) => updateValues({ truckId: truck?._id || "" })}
                  getOptionLabel={(truck) => formatLicensePlateInput(truck.licensePlate)}
                  renderInput={(params) => (
                    <TextField {...params} label={t("License Plate" as TranslationKeys)} />
                  )}
                />
              </Box>

              <Box data-testid={TestId.ORDER.DRIVER}>
                <Autocomplete
                  disablePortal
                  options={drivers}
                  value={findEntityById(drivers, values.driverId)}
                  onChange={(_, driver) => updateValues({ driverId: driver?._id || "" })}
                  getOptionLabel={(driver) => driver.name}
                  getOptionKey={getEntityOptionKey}
                  isOptionEqualToValue={isEntityOptionEqual}
                  renderInput={(params) => (
                    <TextField {...params} label={t("DRIVER" as TranslationKeys)} />
                  )}
                />
              </Box>

              <Box sx={{ gridColumn: { sm: "1 / -1" } }} data-testid={TestId.ORDER.LOAD}>
                <Autocomplete
                  disablePortal
                  multiple
                  options={cities}
                  value={selectedLoadCities}
                  onChange={(_, selectedCities) =>
                    updateValues({ loadCityIds: selectedCities.map((city) => city._id) })
                  }
                  getOptionLabel={(city) => city.name}
                  getOptionKey={getEntityOptionKey}
                  isOptionEqualToValue={isEntityOptionEqual}
                  renderInput={(params) => (
                    <TextField {...params} label={t("orders.load" as TranslationKeys)} />
                  )}
                />
              </Box>

              <Box sx={{ gridColumn: { sm: "1 / -1" } }} data-testid={TestId.ORDER.UNLOAD}>
                <Autocomplete
                  disablePortal
                  multiple
                  options={cities}
                  value={selectedUnloadCities}
                  onChange={(_, selectedCities) =>
                    updateValues({ unloadCityIds: selectedCities.map((city) => city._id) })
                  }
                  getOptionLabel={(city) => city.name}
                  getOptionKey={getEntityOptionKey}
                  isOptionEqualToValue={isEntityOptionEqual}
                  renderInput={(params) => (
                    <TextField {...params} label={t("orders.unload" as TranslationKeys)} />
                  )}
                />
              </Box>

              <TextField
                label={t("No. LT/POD" as TranslationKeys)}
                value={values.externalOrderId}
                onChange={(event) => updateValues({ externalOrderId: event.target.value })}
                inputProps={{ "data-testid": TestId.ORDER.EXTERNAL_ID }}
              />

              <Box data-testid={TestId.ORDER.CUSTOMER}>
                <Autocomplete
                  disablePortal
                  options={customers}
                  value={findEntityById(customers, values.customerId)}
                  onChange={(_, customer) => updateValues({ customerId: customer?._id || "" })}
                  getOptionLabel={(customer) => customer.name}
                  getOptionKey={getEntityOptionKey}
                  isOptionEqualToValue={isEntityOptionEqual}
                  renderInput={(params) => (
                    <TextField {...params} label={t("Customer" as TranslationKeys)} />
                  )}
                />
              </Box>

              <DatePicker
                label={t("orders.departureDate" as TranslationKeys)}
                format="dd/MM/yyyy"
                value={departureDatePickerValue}
                onChange={(newValue) => {
                  setDepartureDatePickerValue(newValue);
                  if (!newValue) {
                    updateValues({ departureDate: "" });
                    return;
                  }
                  if (isCompleteDepartureDate(newValue)) {
                    updateValues({ departureDate: formatDepartureDateString(newValue) });
                  }
                }}
                slotProps={{
                  textField: {
                    inputProps: { "data-testid": TestId.ORDER.DEPARTURE_DATE },
                  },
                  field: { clearable: true },
                }}
              />

              <TimePicker
                label={t("orders.departureTime" as TranslationKeys)}
                ampm={false}
                format="HH:mm"
                views={["hours", "minutes"]}
                value={departureTimePickerValue}
                onChange={(newValue) => {
                  setDepartureTimePickerValue(newValue);
                  updateValues({
                    departureTime: newValue ? formatDepartureTimeString(newValue) : "",
                  });
                }}
                slotProps={{
                  textField: {
                    inputProps: { "data-testid": TestId.ORDER.DEPARTURE_TIME },
                  },
                  field: { clearable: true },
                }}
              />
            </Box>
          </LocalizationProvider>
        </DetailSectionCard>
      </Box>

      <DetailSectionCard
        title={t("orders.expensesSection" as TranslationKeys)}
        icon={<PaymentsOutlinedIcon fontSize="small" />}
        iconColor="#2563EB"
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("orders.expensesFormHint" as TranslationKeys)}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <RupiahAmountField
            label={t("orders.expenseUangJalan" as TranslationKeys)}
            value={values.expenseUangJalan}
            onValueChange={(value) => updateValues({ expenseUangJalan: value })}
            inputProps={{ "data-testid": TestId.ORDER.EXPENSE_UANG_JALAN }}
            sx={
              isSingletonExpenseRevisionKey(revisionKeys, "uang_jalan")
                ? expenseRevisionFieldSx
                : undefined
            }
          />

          <RupiahAmountField
            label={t("orders.expenseMob" as TranslationKeys)}
            value={values.expenseMob}
            onValueChange={(value) => updateValues({ expenseMob: value })}
            inputProps={{ "data-testid": TestId.ORDER.EXPENSE_MOB }}
            sx={
              isSingletonExpenseRevisionKey(revisionKeys, "mob")
                ? expenseRevisionFieldSx
                : undefined
            }
          />

          <RupiahAmountField
            label={t("orders.expenseTaktis" as TranslationKeys)}
            value={values.expenseTaktis}
            onValueChange={(value) => updateValues({ expenseTaktis: value })}
            inputProps={{ "data-testid": TestId.ORDER.EXPENSE_TAKTIS }}
            sx={
              isSingletonExpenseRevisionKey(revisionKeys, "taktis")
                ? expenseRevisionFieldSx
                : undefined
            }
          />

          <RupiahAmountField
            label={t("orders.expenseRegistration" as TranslationKeys)}
            value={values.expenseRegistration}
            onValueChange={(value) => updateValues({ expenseRegistration: value })}
            inputProps={{ "data-testid": TestId.ORDER.EXPENSE_REGISTRATION }}
            sx={
              isSingletonExpenseRevisionKey(revisionKeys, "registration")
                ? expenseRevisionFieldSx
                : undefined
            }
          />

          {showMobRoute && (
            <Box
              sx={{
                gridColumn: { sm: "1 / -1" },
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              }}
            >
              <Autocomplete
                disablePortal
                options={cities}
                value={findEntityById(cities, values.expenseMobRouteCityIds[0])}
                onChange={(_, city) =>
                  updateValues({
                    expenseMobRouteCityIds: [
                      city?._id || "",
                      values.expenseMobRouteCityIds[1] || "",
                    ],
                  })
                }
                getOptionLabel={(city) => city.name}
                getOptionKey={getEntityOptionKey}
                isOptionEqualToValue={isEntityOptionEqual}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("orders.expenseMobRouteFrom" as TranslationKeys)}
                    sx={
                      isSingletonExpenseRevisionKey(revisionKeys, "mob")
                        ? expenseRevisionFieldSx
                        : undefined
                    }
                    inputProps={{
                      ...params.inputProps,
                      "data-testid": TestId.ORDER.EXPENSE_MOB_ROUTE,
                    }}
                  />
                )}
              />
              <Autocomplete
                disablePortal
                options={cities}
                value={findEntityById(cities, values.expenseMobRouteCityIds[1])}
                onChange={(_, city) =>
                  updateValues({
                    expenseMobRouteCityIds: [
                      values.expenseMobRouteCityIds[0] || "",
                      city?._id || "",
                    ],
                  })
                }
                getOptionLabel={(city) => city.name}
                getOptionKey={getEntityOptionKey}
                isOptionEqualToValue={isEntityOptionEqual}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("orders.expenseMobRouteTo" as TranslationKeys)}
                    sx={
                      isSingletonExpenseRevisionKey(revisionKeys, "mob")
                        ? expenseRevisionFieldSx
                        : undefined
                    }
                  />
                )}
              />
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <Typography
          variant="subtitle2"
          sx={{ mb: 1, fontWeight: 700 }}
          data-testid={TestId.ORDER.EXPENSE_ADDITIONAL_HEADER}
        >
          {t("orders.additionalExpenses" as TranslationKeys)}
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddAdditionalRow}
          data-testid={TestId.ORDER.EXPENSE_ADDITIONAL_ADD}
          sx={{ mb: 2, textTransform: "none" }}
        >
          {t("orders.addAdditionalExpense" as TranslationKeys)}
        </Button>

        {values.expenseAdditional.map((row, index) => {
          const needsRevisionHighlight = isAdditionalExpenseRevisionKey(
            revisionKeys,
            additionalExpenseIds[index],
          );

          return (
            <Box
              key={`additional-${index}`}
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr auto" },
                mb: 1,
                alignItems: "center",
              }}
              data-testid={TestId.ORDER.EXPENSE_ADDITIONAL_ROW}
            >
              <RupiahAmountField
                label={t("orders.additionalAmount" as TranslationKeys)}
                value={row.amount}
                onValueChange={(value) => handleAdditionalChange(index, "amount", value)}
                inputProps={{ "data-testid": TestId.ORDER.EXPENSE_ADDITIONAL_AMOUNT }}
                sx={needsRevisionHighlight ? expenseRevisionFieldSx : undefined}
              />
              <TextField
                label={t("Notes" as TranslationKeys)}
                value={row.notes}
                onChange={(event) => handleAdditionalChange(index, "notes", event.target.value)}
                inputProps={{ "data-testid": TestId.ORDER.EXPENSE_ADDITIONAL_NOTES }}
                sx={needsRevisionHighlight ? expenseRevisionFieldSx : undefined}
              />
              <IconButton
                aria-label={t("Delete" as TranslationKeys)}
                onClick={() => handleRemoveAdditionalRow(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        })}
      </DetailSectionCard>

      {!hideActions && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
          {actionButtons}
        </Box>
      )}

      {values.departureDate || values.departureTime ? (
        <input
          type="hidden"
          value={combineDepartureTimestamp(values.departureDate, values.departureTime)}
          data-testid={TestId.ORDER.DEPARTURE}
        />
      ) : null}
    </Box>
  );
};

export default OrderForm;
